import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StatoBadge from "@/components/StatoBadge";
import { toast } from "sonner";
import { Filter, X, Trash2, LayoutGrid, List, MessageCircle } from "lucide-react";
import { Plus } from "lucide-react";
import NuovaPraticaDialog from "@/components/NuovaPraticaDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import CestinoDialog from "@/components/CestinoDialog";
import SollecitoDialog from "@/components/SollecitoDialog";
import { PraticaProgress } from "@/components/PraticaProgress";
import { useAuth } from "@/context/AuthContext";

import { eur } from "@/lib/format";
const fdate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const ORD = [
  { v: "creato", l: "Creazione" },
  { v: "attivita", l: "Ultima attività" },
  { v: "pagamento", l: "Pagamento più vecchio" },
  { v: "residuo", l: "Importo residuo" },
];
const statoOf = (p) => p.stato_derivato || "Da avviare";

const sollecitoItem = (p) => ({
  pratica_id: p.id,
  cliente: p.cliente ? `${p.cliente.nome} ${p.cliente.cognome}` : "",
  tipo_pratica: p.tipo_pratica_nome,
  importo_totale: p.importo_totale || 0,
  importo_pagato: p.importo_pagato || 0,
  residuo: (p.importo_totale || 0) - (p.importo_pagato || 0),
  email: (p.cliente?.email || "").trim(),
  telefono: (p.cliente?.telefono || "").trim() || (p.cliente?.referente?.telefono || "").trim(),
  telefono_fonte: (p.cliente?.telefono || "").trim() ? "cliente" : (p.cliente?.referente?.telefono ? "rif" : null),
  ultimo_sollecito: p.ultimo_sollecito,
  ultimo_sollecito_canale: p.ultimo_sollecito_canale,
});

export default function Pratiche() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [tipo, setTipo] = useState("");
  const [stato, setStato] = useState("");
  const [pagF, setPagF] = useState("all");
  const [attive, setAttive] = useState("all");
  const [origF, setOrigF] = useState(new URLSearchParams(window.location.search).get("origine") || "");
  const [riclF, setRiclF] = useState(new URLSearchParams(window.location.search).get("da_riclassificare") || "");
  const [view, setViewState] = useState(() => localStorage.getItem("pratiche_view") || "list");
  const setView = (v) => { setViewState(v); localStorage.setItem("pratiche_view", v); };
  const [ordina, setOrdina] = useState("creato");
  const [segn, setSegn] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [sollecito, setSollecito] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [showCestino, setShowCestino] = useState(false);

  const { data: tipi } = useQuery({ queryKey: ["tipi-pratica"], queryFn: async () => (await api.get("/tipi-pratica")).data });
  const { data: attenzione } = useQuery({ queryKey: ["attenzione"], queryFn: async () => (await api.get("/attenzione")).data, refetchInterval: 60000 });
  const daVedere = (id) => (attenzione?.motivi || {})[id] || "";
  const { data: referenti } = useQuery({ queryKey: ["referenti"], queryFn: async () => (await api.get("/referenti")).data });
  const { data } = useQuery({
    queryKey: ["pratiche", tipo, attive, ordina, segn, origF, riclF],
    queryFn: async () => (await api.get("/pratiche", { params: { tipo: tipo || undefined, attive, ordina, referente_id: segn || undefined, origine: origF || undefined, da_riclassificare: riclF || undefined } })).data,
  });

  const selectedTipo = (tipi || []).find((t) => t.nome === tipo);
  const statoOptions = selectedTipo
    ? (selectedTipo.steps || []).map((s) => s.nome)
    : Array.from(new Set((tipi || []).flatMap((t) => (t.steps || []).map((s) => s.nome))));

  let pratiche = data || [];
  if (stato) pratiche = pratiche.filter((p) => statoOf(p) === stato);
  if (pagF === "senza") pratiche = pratiche.filter((p) => (p.importo_pagato || 0) <= 0);
  if (pagF === "con") pratiche = pratiche.filter((p) => (p.importo_pagato || 0) > 0);

  const activeFilters = (tipo ? 1 : 0) + (stato ? 1 : 0) + (origF ? 1 : 0) + (pagF !== "all" ? 1 : 0) + (attive !== "all" ? 1 : 0) + (segn ? 1 : 0);
  const clearFilters = () => { setTipo(""); setStato(""); setPagF("all"); setAttive("all"); setSegn(""); };

  const removePratica = (e, p) => {
    e.stopPropagation();
    setToDelete(p);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/pratiche/${toDelete.id}`);
      toast.success("Pratica spostata nel cestino");
      qc.invalidateQueries({ queryKey: ["pratiche"] });
      qc.invalidateQueries({ queryKey: ["contabilita"] });
      qc.invalidateQueries({ queryKey: ["pratiche-cestino"] });
    } catch (err) { toast.error(err?.response?.data?.detail || "Eliminazione non riuscita"); }
    finally { setToDelete(null); }
  };

  const selectCls = "bg-white border border-[#C6C3BA] rounded-[4px] px-3 py-2 text-[14px] focus:outline-none focus:border-[#A8863F]";
  const etichetta = "text-[11px] font-bold uppercase tracking-[0.6px] text-[#5A5A5A]";
  const aperte = pratiche.filter((p) => !["Conclusa", "Archiviata"].includes(statoOf(p))).length;

  const Scheda = ({ p }) => {
    const motivo = daVedere(p.id);
    return (
      <div data-testid="pratica-grid-item" onClick={() => navigate(`/pratiche/${p.id}`)}
        title={motivo ? `Da completare: ${motivo}` : undefined}
        className={`sez p-4 sm:p-5 cursor-pointer transition-colors hover:border-[#A8863F] ${motivo ? "border-l-[3px] border-l-[#9A3B13]" : ""}`}
        style={motivo ? { background: "#FDF6F1" } : undefined}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span data-testid="attiva-dot" title={p.attiva ? "ON" : "OFF"}
              className={`inline-block w-2 h-2 rounded-full shrink-0 ${p.attiva ? "bg-[#2E5E3A]" : "bg-[#C6C3BA]"}`} />
            <span className="text-[16px] font-bold text-[#1B2A4B] leading-snug break-words">
              {p.cliente ? `${p.cliente.nome} ${p.cliente.cognome}` : "—"}
            </span>
          </div>
          {can("elimina_pratiche") && (
            <button data-testid={`pratica-delete-${p.id}`} onClick={(e) => removePratica(e, p)} title="Elimina pratica"
              className="shrink-0 -mt-1 -mr-1 p-1.5 text-[#8A2E2E]/60 hover:text-[#8A2E2E] transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="text-[14px] text-[#1A1A1A] mt-1 break-words">{p.tipo_pratica_nome}</div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <StatoBadge stato={statoOf(p)} testid={`stato-g-${p.id}`} />
          {p.da_riclassificare && (
            <span data-testid={`da-riclassificare-${p.id}`} title="Tipo pratica fuori catalogo: da riclassificare a mano"
              className="text-[12px] font-bold text-[#8A2E2E]">da riclassificare</span>
          )}
          {(p.documenti_mancanti || []).length > 0 && (
            <span data-testid={`badge-attesa-doc-${p.id}`}
              title={(p.documenti_mancanti || []).map((dm) => `${dm.nome}${dm.motivo ? ` (${dm.motivo})` : ""}`).join(" · ")}
              className="text-[12px] font-bold text-[#9A3B13]">
              {p.documenti_mancanti.length} document{p.documenti_mancanti.length === 1 ? "o" : "i"} mancant{p.documenti_mancanti.length === 1 ? "e" : "i"}
            </span>
          )}
          {p.nasce_da && (
            <span data-testid={`nasce-da-${p.id}`} className="text-[12px] text-[#8A6E2F]">
              nasce da {p.nasce_da.tipo} · {p.nasce_da.data}
            </span>
          )}
          <span className="text-[12px] text-[#5A5A5A]">aperta il {fdate(p.created_at)}</span>
        </div>
        <div className="mt-3.5 pt-3 border-t border-[#F0EEE8] grid grid-cols-2 gap-2">
          <div>
            <div className={etichetta}>Valore pratica</div>
            <div className="text-[16px] font-bold text-[#1B2A4B]">{eur(p.importo_totale)}</div>
          </div>
          <div className="text-right">
            <div className={etichetta}>Da incassare</div>
            <div className="flex items-center justify-end gap-2">
              <span className={`text-[16px] font-bold ${p.importo_totale - p.importo_pagato > 0 ? "text-[#1A1A1A]" : "text-[#2E5E3A]"}`}>
                {eur(p.importo_totale - p.importo_pagato)}
              </span>
              {p.importo_totale - p.importo_pagato > 0 && (
                <button data-testid={`pratica-sollecito-${p.id}`} title="Sollecita il pagamento"
                  onClick={(e) => { e.stopPropagation(); setSollecito(sollecitoItem(p)); }}
                  className="text-[#2E5E3A]/70 hover:text-[#2E5E3A] transition-colors">
                  <MessageCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="pratiche-page"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex flex-col gap-0.5">
            <h1 className="text-[25px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B]">Pratiche</h1>
            <p className="text-[14px] md:text-[16px] text-[#4A4A48]" data-testid="pratiche-count">
              {pratiche.length} {pratiche.length === 1 ? "pratica" : "pratiche"} · {aperte} in corso
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button data-testid="new-pratica-btn" onClick={() => setOpenNew(true)} className="btn btn-g btn-larghezza sm:w-auto">
              <Plus className="w-4 h-4" /> Nuova pratica
            </button>
            {can("elimina_pratiche") && (
              <button data-testid="open-cestino-btn" onClick={() => setShowCestino(true)} className="btn btn-o btn-larghezza sm:w-auto">
                <Trash2 className="w-4 h-4" /> Cestino
              </button>
            )}
          </div>
        </header>

        <div className="sez px-4 py-3 flex items-center justify-between flex-wrap gap-2.5" data-testid="pratiche-utility-bar">
          <button data-testid="toggle-filters" onClick={() => setShowFilters((v) => !v)}
            className={`btn btn-piccolo ${showFilters || activeFilters > 0 ? "btn-p" : "btn-o"}`}>
            <Filter className="w-4 h-4" /> Filtri{activeFilters > 0 ? ` · ${activeFilters}` : ""}
          </button>
          <div className="hidden md:flex gap-2.5" data-testid="view-toggle">
            <button data-testid="view-list" onClick={() => setView("list")} title="Vista elenco"
              className={`btn btn-piccolo ${view === "list" ? "btn-p" : "btn-o"}`}><List className="w-4 h-4" /> Elenco</button>
            <button data-testid="view-grid" onClick={() => setView("grid")} title="Vista schede"
              className={`btn btn-piccolo ${view === "grid" ? "btn-p" : "btn-o"}`}><LayoutGrid className="w-4 h-4" /> Schede</button>
          </div>
        </div>

        {showFilters && (
          <div className="sez p-4 sm:p-5 flex flex-col gap-3.5" data-testid="filters-panel">
            <div className="flex items-center justify-between gap-2.5">
              <span className="eti">Filtri</span>
              {activeFilters > 0 && (
                <button data-testid="clear-filters" onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#8A6E2F] hover:text-[#8A2E2E]">
                  <X className="w-3.5 h-3.5" /> Azzera ({activeFilters})
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3.5">
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Tipo pratica</label>
                <select data-testid="filter-tipo" value={tipo} onChange={(e) => { setTipo(e.target.value); setStato(""); }} className={`${selectCls} w-52`}>
                  <option value="">Tutti i tipi</option>
                  {(tipi || []).map((t) => <option key={t.id} value={t.nome}>{t.nome}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Origine</label>
                <select data-testid="filter-origine" value={origF} onChange={(e) => setOrigF(e.target.value)} className={`${selectCls} w-52`}>
                  <option value="">Tutte le origini</option>
                  <option value="consulenza_iniziale">Da consulenza iniziale</option>
                  <option value="diretta">Diretta</option>
                  <option value="spazio_ascolto_hirundo">Spazio ascolto Hirundo</option>
                  <option value="segnalazione">Segnalazione</option>
                  <option value="pratica_collegata">Pratica collegata</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Riclassifica</label>
                <select data-testid="filter-riclassificare" value={riclF} onChange={(e) => setRiclF(e.target.value)} className={`${selectCls} w-44`}>
                  <option value="">Tutte</option>
                  <option value="on">Solo da riclassificare</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Stato (step)</label>
                <select data-testid="filter-stato" value={stato} onChange={(e) => setStato(e.target.value)} className={`${selectCls} w-56`}>
                  <option value="">Tutti gli stati</option>
                  <option value="Da avviare">Da avviare</option>
                  {statoOptions.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Acconti</label>
                <select data-testid="filter-pagamenti" value={pagF} onChange={(e) => setPagF(e.target.value)} className={selectCls}>
                  <option value="all">Tutti</option>
                  <option value="senza">Senza acconti</option>
                  <option value="con">Con acconti</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Rif.</label>
                <select data-testid="filter-segnalatore" value={segn} onChange={(e) => setSegn(e.target.value)} className={`${selectCls} w-52`}>
                  <option value="">Tutti i segnalatori</option>
                  <option value="studio">RB Studio</option>
                  {(referenti || []).map((r) => <option key={r.id} value={r.id}>{[r.cognome, r.nome].filter(Boolean).join(" ")}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Ordina</label>
                <select data-testid="sort-select" value={ordina} onChange={(e) => setOrdina(e.target.value)} className={selectCls}>
                  {ORD.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={etichetta}>Attività</label>
                <div className="flex gap-2">
                  {[["all", "Tutte"], ["on", "ON"], ["off", "OFF"]].map(([v, l]) => (
                    <button key={v} data-testid={`filter-attive-${v}`} onClick={() => setAttive(v)}
                      className={`btn btn-piccolo ${attive === v ? "btn-p" : "btn-o"}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Telefono: sempre a schede */}
        <div className="md:hidden flex flex-col gap-[18px]">
          {pratiche.length === 0 && <p className="text-[14px] text-[#5A5A5A]">Nessuna pratica trovata con questi filtri.</p>}
          {pratiche.map((p) => <Scheda key={p.id} p={p} />)}
        </div>

        <div className="hidden md:block">
          {view === "grid" ? (
            <div className="grid grid-cols-2 gap-4" data-testid="pratiche-grid">
              {pratiche.length === 0 && <p className="col-span-full text-[14px] text-[#5A5A5A]">Nessuna pratica trovata con questi filtri.</p>}
              {pratiche.map((p) => <Scheda key={p.id} p={p} />)}
            </div>
          ) : (
            <div className="sez overflow-hidden" data-testid="pratiche-elenco">
              <div className="subt">
                <span>Elenco</span>
                <span className="text-[12px] font-normal normal-case tracking-normal text-[#5A5A5A]">{pratiche.length} pratiche</span>
              </div>
              {pratiche.length === 0 && (
                <div className="riga text-[#5A5A5A]">Nessuna pratica trovata con questi filtri.</div>
              )}
              {pratiche.map((p) => {
                const motivo = daVedere(p.id);
                const residuo = p.importo_totale - p.importo_pagato;
                return (
                  <div key={p.id} data-testid="pratica-row" onClick={() => navigate(`/pratiche/${p.id}`)}
                    title={motivo ? `Da completare: ${motivo}` : undefined}
                    className={`riga group cursor-pointer gap-4 hover:bg-[#FAFAF7] transition-colors ${motivo ? "border-l-[3px] border-l-[#9A3B13]" : ""}`}
                    style={motivo ? { background: "#FDF6F1" } : undefined}>
                    <span data-testid="attiva-dot" title={p.attiva ? "ON" : "OFF"}
                      className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${p.attiva ? "bg-[#2E5E3A]" : "bg-[#C6C3BA]"}`} />
                    <span className="flex-[999_1_240px] min-w-0 flex flex-col gap-0.5">
                      <span className="text-[15px] font-bold text-[#1B2A4B] break-words">
                        {p.cliente ? `${p.cliente.nome} ${p.cliente.cognome}` : "—"}
                      </span>
                      <span className="text-[13px] text-[#4A4A48] flex items-center gap-2 flex-wrap">
                        <span className="truncate">{p.tipo_pratica_nome}</span>
                        <StatoBadge stato={statoOf(p)} testid={`stato-l-${p.id}`} />
                        {p.da_riclassificare && (
                          <span data-testid={`da-riclassificare-${p.id}`} className="font-bold text-[#8A2E2E]">da riclassificare</span>
                        )}
                        {(p.documenti_mancanti || []).length > 0 && (
                          <span data-testid={`badge-attesa-doc-${p.id}`} className="font-bold text-[#9A3B13]"
                            title={(p.documenti_mancanti || []).map((dm) => `${dm.nome}${dm.motivo ? ` (${dm.motivo})` : ""}`).join(" · ")}>
                            {p.documenti_mancanti.length} document{p.documenti_mancanti.length === 1 ? "o" : "i"} mancant{p.documenti_mancanti.length === 1 ? "e" : "i"}
                          </span>
                        )}
                      </span>
                      <span className="text-[12px] text-[#5A5A5A]">
                        Rif. {p.cliente?.referente ? [p.cliente.referente.cognome, p.cliente.referente.nome].filter(Boolean).join(" ") : "RB Studio"}
                        {" · aperta il "}{fdate(p.created_at)}
                      </span>
                    </span>
                    <span className="hidden lg:block w-[150px] shrink-0"><PraticaProgress steps={p.steps || []} compact /></span>
                    <span className="w-[120px] shrink-0 text-right">
                      <span className={`text-[15px] font-bold ${residuo > 0 ? "text-[#1A1A1A]" : "text-[#2E5E3A]"}`}>{eur(residuo)}</span>
                      <span className="block text-[12px] text-[#5A5A5A]">{residuo > 0 ? "da incassare" : "tutto incassato"}</span>
                    </span>
                    <span className="shrink-0 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {residuo > 0 && (
                        <button data-testid={`pratica-sollecito-${p.id}`} title="Sollecita il pagamento"
                          onClick={(e) => { e.stopPropagation(); setSollecito(sollecitoItem(p)); }}
                          className="text-[#2E5E3A]/70 hover:text-[#2E5E3A]"><MessageCircle className="w-4 h-4" /></button>
                      )}
                      {can("elimina_pratiche") && (
                        <button data-testid={`pratica-delete-${p.id}`} title="Elimina pratica"
                          onClick={(e) => removePratica(e, p)}
                          className="text-[#8A2E2E]/60 hover:text-[#8A2E2E]"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {sollecito && <SollecitoDialog it={sollecito} onClose={() => setSollecito(null)} />}
      {openNew && <NuovaPraticaDialog onClose={() => setOpenNew(false)} />}
      {showCestino && <CestinoDialog onClose={() => setShowCestino(false)} />}
      {toDelete && (
        <ConfirmDialog title="Spostare nel cestino?"
          message={`La pratica "${toDelete.tipo_pratica_nome}"${toDelete.cliente ? ` di ${toDelete.cliente.nome} ${toDelete.cliente.cognome}` : ""} verrà spostata nel cestino (recuperabile per 30 giorni).\nIl cliente e i documenti collegati NON verranno eliminati.`}
          confirmLabel="Sposta nel cestino" onConfirm={confirmDelete} onClose={() => setToDelete(null)} />
      )}
    </div>
  );
}
